import { useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { isActionOf } from "typesafe-actions";

import { useDidUpdate } from "app/custom-hooks/useDidUpdate";
import {
    profileModalIsOpenedSetAction,
    profileSelectedAction,
    setProfileForEditAction,
} from "app/pages/sales-intelligence/modules/company-analysis/store/contacts/action-creators";
import {
    selectProfileForEdit,
    selectProfileIsBeingSubmitted,
    selectProfileModalIsOpened,
} from "app/pages/sales-intelligence/modules/company-analysis/store/contacts/selectors";

import RemoveProfileModal from "../contacts-search-profiles/components/RemoveProfileModal/RemoveProfileModal";
import { isSharedEmployeeProfile } from "../contacts-search-profiles/helpers/accessLevelFilters";
import {
    createProfileAsync,
    deleteProfileAsync,
    excludeProfileAsync,
    resetCreatedProfileAction,
    updateProfileAsync,
} from "../contacts-search-profiles/store/action-creators";
import {
    createProfileThunk,
    deleteProfileThunk,
    excludeProfileThunk,
    updateProfileThunk,
} from "../contacts-search-profiles/store/effects";
import { selectProfileIsDeleting, selectProfileIsExcluding } from "../contacts-search-profiles/store/selectors";
import type { Profile } from "../contacts-search-profiles/types/common";
import { ProfileAccessLevel } from "../contacts-search-profiles/types/dto";
import type { ProfileDto } from "../contacts-search-profiles/types/dto";

import ProfileModal from "./ProfileModal";

const ProfileModalContainer = () => {
    const dispatch = useDispatch();
    const isBeingSubmitted = useSelector(selectProfileIsBeingSubmitted);

    const isBeingDeleted = useSelector(selectProfileIsDeleting);
    const isBeingExcluded = useSelector(selectProfileIsExcluding);

    const profileForEdit = useSelector(selectProfileForEdit);

    /**
     * Similarweb-provided defaults cannot be changed in place: editing one saves a private copy and
     * hides the original for this user. Account-shared personas are updated like any other persona.
     */
    const isSimilarwebProfile = profileForEdit?.accessLevel === ProfileAccessLevel.Global;

    const isModalOpened = useSelector(selectProfileModalIsOpened);
    const [isDeleteModalOpened, setDeleteModalOpened] = useState(false);

    useDidUpdate(() => {
        if (isModalOpened) {
            dispatch(resetCreatedProfileAction());

            return;
        }

        dispatch(setProfileForEditAction(null));
    }, [isModalOpened]);

    async function handleProfileCreate(profile: Omit<ProfileDto, "id">) {
        const resultAction = await dispatch(createProfileThunk(profile));
        const isSuccessAction = isActionOf(createProfileAsync.success);

        if (isSuccessAction(resultAction)) {
            const { payload } = resultAction;

            dispatch(profileModalIsOpenedSetAction(false));
            dispatch(profileSelectedAction(payload.id));
        }
    }

    async function handleSimilarwebProfileUpdate(payload: Omit<ProfileDto, "id">) {
        const resultAction = await dispatch(createProfileThunk(payload));
        const isSuccessAction = isActionOf(createProfileAsync.success);

        if (isSuccessAction(resultAction)) {
            const isSuccessAction = isActionOf(excludeProfileAsync.success);
            const { payload } = resultAction;
            const excludeResultAction = await dispatch(excludeProfileThunk(profileForEdit?.id as ProfileDto["id"]));

            if (isSuccessAction(excludeResultAction)) {
                dispatch(profileModalIsOpenedSetAction(false));
                dispatch(profileSelectedAction(payload.id));
            }
        }
    }

    async function handleProfileUpdate(payload: Omit<ProfileDto, "id">) {
        if (isSimilarwebProfile) {
            return handleSimilarwebProfileUpdate(payload);
        }

        const resultAction = await dispatch(
            updateProfileThunk({
                ...payload,
                id: profileForEdit?.id as ProfileDto["id"],
                // The modal has no visibility control, so an edit must not change who the persona belongs to.
                isShared: isSharedEmployeeProfile(profileForEdit as ProfileDto),
            }),
        );
        const isSuccessAction = isActionOf(updateProfileAsync.success);

        if (isSuccessAction(resultAction)) {
            const { payload } = resultAction;

            dispatch(profileModalIsOpenedSetAction(false));
            dispatch(profileSelectedAction(payload.id));
        }
    }

    async function handleSimilarwebProfileDelete(id: ProfileDto["id"]) {
        const excludeAction = await dispatch(excludeProfileThunk(id));
        const isSuccessAction = isActionOf(excludeProfileAsync.success);

        if (isSuccessAction(excludeAction)) {
            setDeleteModalOpened(false);
            dispatch(profileModalIsOpenedSetAction(false));
            dispatch(profileSelectedAction(null));
        }
    }

    async function handleRegularProfileDelete(id: ProfileDto["id"]) {
        const deleteAction = await dispatch(deleteProfileThunk(id));
        const isSuccessAction = isActionOf(deleteProfileAsync.success);

        if (isSuccessAction(deleteAction)) {
            setDeleteModalOpened(false);
            dispatch(profileModalIsOpenedSetAction(false));
            dispatch(profileSelectedAction(null));
        }
    }

    function handleProfileDelete(id: ProfileDto["id"]) {
        if (isSimilarwebProfile) {
            return handleSimilarwebProfileDelete(id);
        }

        return handleRegularProfileDelete(id);
    }

    function handleSubmit(payload: Omit<ProfileDto, "id">) {
        if (profileForEdit === null) {
            void handleProfileCreate(payload);
        } else {
            void handleProfileUpdate(payload);
        }
    }

    function handleCancel() {
        dispatch(profileModalIsOpenedSetAction(false));
    }

    function handleDeleteCancel() {
        setDeleteModalOpened(false);
    }

    function handleDeleteClick() {
        setDeleteModalOpened(true);
    }

    return (
        <>
            <ProfileModal
                profile={profileForEdit}
                isBeingDeleted={isBeingDeleted || isBeingExcluded}
                isBeingSubmitted={isBeingSubmitted}
                isOpened={isModalOpened ? !isDeleteModalOpened : false}
                onCancel={handleCancel}
                onSubmit={handleSubmit}
                onDeleteClick={handleDeleteClick}
            />
            <RemoveProfileModal
                profile={profileForEdit as Profile}
                isLoading={isBeingDeleted}
                isOpened={isDeleteModalOpened}
                onSubmit={handleProfileDelete}
                onCancel={handleDeleteCancel}
            />
        </>
    );
};

export default ProfileModalContainer;
